import React, { useMemo, useState } from "react";
import { TEMPLATE_COLUMNS } from "./reportColumnCatalog";

export default function ReportTemplateColumnsEditor({
  columnOrder = [],
  visibleColumns = {},
  columnLabels = {},
  onChange,
}) {
  const fullOrder = useMemo(() => {
    const validKeys = new Set(TEMPLATE_COLUMNS.map((c) => c.viewKey));
    const have = new Set();
    const saved = columnOrder.filter((key) => {
      if (!validKeys.has(key) || have.has(key)) return false;
      have.add(key);
      return true;
    });
    return [...saved, ...TEMPLATE_COLUMNS.map((c) => c.viewKey).filter((key) => !have.has(key))];
  }, [columnOrder]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dragOverKey, setDragOverKey] = useState(null);

  const byKey = useMemo(() => {
    const map = {};
    for (const column of TEMPLATE_COLUMNS) map[column.viewKey] = column;
    return map;
  }, []);

  const emit = (next) => {
    onChange?.({
      columnOrder: next.columnOrder ?? fullOrder,
      visibleColumns: next.visibleColumns ?? visibleColumns,
      columnLabels: next.columnLabels ?? columnLabels,
    });
  };

  const isEnabled = (key) => visibleColumns[key] !== false;
  const selectedKeys = fullOrder.filter(isEnabled);
  const selectedCount = selectedKeys.length;

  const toggle = (viewKey) => {
    const currentlyEnabled = isEnabled(viewKey);
    if (currentlyEnabled && selectedCount <= 1) return;
    emit({ visibleColumns: { ...visibleColumns, [viewKey]: !currentlyEnabled } });
  };

  const setLabel = (viewKey, value) => {
    emit({ columnLabels: { ...columnLabels, [viewKey]: value } });
  };

  // Reorder only selected fields within the slots occupied by selected fields.
  // Hidden fields remain in the persisted columnOrder array and keep their slots.
  const reorderSelected = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    const selected = fullOrder.filter(isEnabled);
    const sourceIndex = selected.indexOf(sourceKey);
    const targetIndex = selected.indexOf(targetKey);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const reordered = [...selected];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    let selectedIndex = 0;
    const nextOrder = fullOrder.map((key) =>
      isEnabled(key) ? reordered[selectedIndex++] : key,
    );
    emit({ columnOrder: nextOrder });
  };

  const move = (viewKey, direction) => {
    const selected = fullOrder.filter(isEnabled);
    const index = selected.indexOf(viewKey);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= selected.length) return;
    reorderSelected(viewKey, selected[target]);
  };

  const reset = () => {
    const allVisible = {};
    for (const column of TEMPLATE_COLUMNS) allVisible[column.viewKey] = true;
    emit({
      columnOrder: TEMPLATE_COLUMNS.map((column) => column.viewKey),
      visibleColumns: allVisible,
      columnLabels: {},
    });
  };

  const groupedColumns = useMemo(() => {
    const groups = [];
    for (const column of TEMPLATE_COLUMNS) {
      let group = groups.find((item) => item.name === column.group);
      if (!group) {
        group = { name: column.group, columns: [] };
        groups.push(group);
      }
      group.columns.push(column);
    }
    return groups;
  }, []);

  return (
    <div className="r6-tpl-cols">
      <div className="r6-tpl-cols-head">
        <div className="r6-tpl-cols-title">
          Selected columns
          <span className="r6-tpl-cols-count">
            {selectedCount} of {TEMPLATE_COLUMNS.length}
          </span>
        </div>
        <div className="r6-tpl-cols-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setDrawerOpen(true)}>
            <i className="fas fa-plus" /> Add columns
          </button>
          <button type="button" className="r6-link" onClick={reset}>
            Reset all
          </button>
        </div>
      </div>

      <p className="r6-tpl-cols-help">
        Drag to reorder columns, edit their report labels, or remove fields you do not need.
      </p>

      {selectedKeys.length === 0 ? (
        <div className="r6-tpl-cols-empty">Select at least one column to include in the report.</div>
      ) : (
        <ul className="r6-tpl-cols-list r6-tpl-cols-selected-list">
          {selectedKeys.map((viewKey, index) => {
            const def = byKey[viewKey];
            if (!def) return null;
            const label = columnLabels[viewKey] || def.label;
            return (
              <li
                key={viewKey}
                className={`r6-tpl-col-row r6-tpl-col-row-selected ${dragOverKey === viewKey ? "is-drag-over" : ""}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setDragOverKey(viewKey);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  reorderSelected(event.dataTransfer.getData("text/plain"), viewKey);
                  setDragOverKey(null);
                }}
              >
                <button
                  type="button"
                  className="r6-tpl-drag-handle"
                  draggable
                  title="Drag to reorder column"
                  aria-label={`Drag ${label} to reorder`}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", viewKey);
                    setDragOverKey(null);
                  }}
                  onDragEnd={() => setDragOverKey(null)}
                >
                  <i className="fas fa-grip-vertical" />
                </button>
                <span className="r6-tpl-col-position">{index + 1}.</span>
                <span className="r6-tpl-col-group">{def.group}</span>
                <input
                  className="form-control form-control-sm r6-tpl-col-label"
                  value={label}
                  placeholder={def.label}
                  aria-label={`Report label for ${def.label}`}
                  onChange={(event) => setLabel(viewKey, event.target.value)}
                />
                <div className="r6-tpl-col-moves">
                  <button type="button" className="r6-tpl-move" onClick={() => move(viewKey, "up")} disabled={index === 0} title="Move up">
                    <i className="fas fa-chevron-up" />
                  </button>
                  <button type="button" className="r6-tpl-move" onClick={() => move(viewKey, "down")} disabled={index === selectedKeys.length - 1} title="Move down">
                    <i className="fas fa-chevron-down" />
                  </button>
                  <button
                    type="button"
                    className="r6-tpl-remove-column"
                    onClick={() => toggle(viewKey)}
                    disabled={selectedCount <= 1}
                    title={selectedCount <= 1 ? "At least one column is required" : `Remove ${label}`}
                    aria-label={`Remove ${label}`}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {drawerOpen && (
        <div className="r6-tpl-drawer-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setDrawerOpen(false);
        }}>
          <aside className="r6-tpl-columns-drawer" role="dialog" aria-modal="true" aria-labelledby="r6-tpl-drawer-title">
            <div className="r6-tpl-drawer-head">
              <div>
                <h2 id="r6-tpl-drawer-title">Available columns</h2>
                <p>{selectedCount} of {TEMPLATE_COLUMNS.length} columns selected</p>
              </div>
              <button type="button" className="r6-tpl-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close available columns">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="r6-tpl-drawer-list">
              {groupedColumns.map((group) => (
                <section className="r6-tpl-drawer-group" key={group.name}>
                  <h3>{group.name}</h3>
                  {group.columns.map((column) => {
                    const selected = isEnabled(column.viewKey);
                    return (
                      <label
                        key={column.viewKey}
                        className={`r6-tpl-drawer-option ${selected ? "is-selected" : ""} ${selected && selectedCount <= 1 ? "is-last-selected" : ""}`}
                        title={selected && selectedCount <= 1 ? "At least one column is required" : selected ? `Unselect ${column.label}` : `Add ${column.label}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={selected && selectedCount <= 1}
                          onChange={() => toggle(column.viewKey)}
                          aria-label={`${selected ? "Remove" : "Add"} ${column.label} ${selected && selectedCount <= 1 ? "(at least one column is required)" : ""}`}
                        />
                        <span>{column.label}</span>
                        {selected && <span className="r6-tpl-drawer-selected-label">{selectedCount <= 1 ? "Required" : "Selected"}</span>}
                      </label>
                    );
                  })}
                </section>
              ))}
            </div>
            <div className="r6-tpl-drawer-footer">
              <span>{selectedCount} columns selected</span>
              <button type="button" className="btn btn-primary" onClick={() => setDrawerOpen(false)}>Done</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
